import { round } from 'lodash'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { asyncForEach } from 'src/utils/asyncUtils'
import { getProjectQuoteTotals } from 'src/utils/quote/quoteUtil'
import {
	aicpSubCategories,
	categories,
	photoGridSubCategories,
	soundProdSubCategories,
} from 'src/utils/quoteGridUtils/createQuoteUtil'
import * as queries from '../graphql/queries'
import {
	fetchProjectsByParentProjectIdUtil,
	fetchQuotesByParentProjectIdUtil,
} from './fetchUtils'

function getProjectQuoteCategories({ projectId, quoteType }) {
	// currently returns the list of categories from the one available template
	switch (quoteType) {
		case 'frenchApfp':
			return categories
		case 'frenchPostProd':
			return categories
		case 'aicp':
			// ! Aicp is broken until totals api is fixed
			return aicpSubCategories
		case 'photoGrid': {
			// ! We are doing this because project pdf is specifically looking for category names and not subCategory Names we get from photoGrid and aicp
			const newPhotoGridSubCategories = photoGridSubCategories.map(
				(c) => {
					c.category = c.subCategory
					c.category_fr = c.subCategory_fr
					c.categoryPrefix = c.subCategoryPrefix
					c.categorySuffix = c.subCategorySuffix
					return c
				}
			)
			return newPhotoGridSubCategories
		}
		case 'soundProd': {
			// ! We are doing this because project pdf is specifically looking for category names and not subCategory Names we get from photoGrid and aicp
			const newSoundProdSubCategories = soundProdSubCategories.map(
				(c) => {
					c.category = c.subCategory
					c.category_fr = c.subCategory_fr
					c.categoryPrefix = c.subCategoryPrefix
					c.categorySuffix = c.subCategorySuffix
					return c
				}
			)
			return newSoundProdSubCategories
		}
		default:
			return categories
	}
}

async function getStarredQuotes({ projectId, fetchFilter }) {
	// Finds the quotes that are starred
	const filterQuote = {
		and: [
			{ activeFlag: { eq: true } },
			{ isStarred: { eq: true } },
			{
				_deleted: {
					attributeExists: false,
				},
			},
		],
	}
	const starredQuotesArray = await fetchQuotesByParentProjectIdUtil(
		projectId,
		filterQuote
	)

	const projectFilter = {}
	projectFilter.parentProjectId = projectId
	projectFilter.activeFlag = true
	// Goes through all the child projects through recursion and finds starred quotes in it

	const childProjects = await fetchProjectsByParentProjectIdUtil(
		projectId,
		fetchFilter
	)

	for (const project of childProjects) {
		const childProjectStarredQuotes = await getStarredQuotes({
			projectId: project.id,
			fetchFilter,
		})
		for (const quote of childProjectStarredQuotes) {
			starredQuotesArray.push(quote)
		}
	}
	return starredQuotesArray
}

async function getProjectTotals({ projectId, fetchFilter }) {
	// Getting the project from the id
	const {
		data: { getProjects },
	} = await API.graphql(
		graphqlOperation(queries.getProjects, { id: projectId })
	)

	const project = getProjects
	// Creating an empty totals object for the project
	const totalsObject = {
		id: null,
		name: null,
		quotes: [],
		categoryTotals: [],
		totals: {},
		franceTotals: {},
		internationalTotals: {},
		discount: null,
	}
	// If project doesn't exist it sends the empty totals object
	if (!project) return totalsObject
	// set the project id and name
	totalsObject.id = project.id
	totalsObject.name = project.name
	// initialize the project totals object
	// Used to have the totals for budget, actuals & provisional includes margin, sub totals & margin %
	let totals = getTotalTemplateObject()
	let franceTotals = getTotalTemplateObject()
	let internationalTotals = getTotalTemplateObject()
	// Creates the totals object for the discount
	let discount = getDiscountTemplateObject()
	// get the starred quotes inside the project and child projects
	const starredQuotes = await getStarredQuotes({ projectId, fetchFilter })

	// By default keep the quoteType to frenchApfp
	let quoteType = 'frenchApfp'
	if (starredQuotes.length > 0 && starredQuotes[0].quoteType) {
		// Sets the quoteType to be the first Starred quote
		quoteType = starredQuotes[0].quoteType
	}

	// get the category totals template array for all categories
	let categoryTotalsArray = getCategoryTotalsTemplateArray({
		projectId,
		quoteType,
		franceOrInternational: null,
	})

	let franceCategoryTotalsArray = getCategoryTotalsTemplateArray({
		projectId,
		quoteType,
		franceOrInternational: 'f',
	})

	let internationalCategoryTotalsArray = getCategoryTotalsTemplateArray({
		projectId,
		quoteType,
		franceOrInternational: 'i',
	})

	await asyncForEach(starredQuotes, async (quote) => {
		const quoteTotalsObject = await getProjectQuoteTotals({
			quoteId: quote.id,
			fetchFilter,
		})

		totalsObject.quotes.push(quoteTotalsObject)
		// update the project's category totals arrays
		categoryTotalsArray =
			updateProjectCategoryTotalsArrayFromQuoteTotalsArray({
				projectCategoryTotalsArray: categoryTotalsArray,
				quoteCategoryTotalsArray: quoteTotalsObject.categoryTotals,
				quoteType: quote.quoteType,
			})
		franceCategoryTotalsArray =
			updateProjectCategoryTotalsArrayFromQuoteTotalsArray({
				projectCategoryTotalsArray: franceCategoryTotalsArray,
				quoteCategoryTotalsArray:
					quoteTotalsObject.franceCategoryTotals,
				quoteType: quote.quoteType,
			})
		internationalCategoryTotalsArray =
			updateProjectCategoryTotalsArrayFromQuoteTotalsArray({
				projectCategoryTotalsArray: internationalCategoryTotalsArray,
				quoteCategoryTotalsArray:
					quoteTotalsObject.internationalCategoryTotals,
				quoteType: quote.quoteType,
			})
		// update the project's totals objects
		totals = updateProjectTotalsObjectFromQuoteTotalsObject(
			totals,
			quoteTotalsObject.totals
		)
		franceTotals = updateProjectTotalsObjectFromQuoteTotalsObject(
			franceTotals,
			quoteTotalsObject.franceTotals
		)
		internationalTotals = updateProjectTotalsObjectFromQuoteTotalsObject(
			internationalTotals,
			quoteTotalsObject.internationalTotals
		)
		if (quoteTotalsObject.discount) {
			discount = updateProjectDiscountObjectFromQuoteDiscountObject(
				discount,
				quoteTotalsObject.discount
			)
		}
	})
	totalsObject.categoryTotals = categoryTotalsArray
	totalsObject.franceCategoryTotals = franceCategoryTotalsArray
	totalsObject.internationalCategoryTotals = internationalCategoryTotalsArray
	totalsObject.totals = calculateProjectMarginPercentage(totals)
	totalsObject.franceTotals = calculateProjectMarginPercentage(franceTotals)
	totalsObject.internationalTotals =
		calculateProjectMarginPercentage(internationalTotals)
	totalsObject.discount = calculateProjectDiscount(discount, totals)
	return totalsObject
}

function calculateProjectMarginPercentage(projectTotalsObject) {
	const localProjectTotalsObject = { ...projectTotalsObject }
	// update margin percentages
	if (localProjectTotalsObject.budgetGrandTotal) {
		localProjectTotalsObject.budgetMarginPercentage = round(
			(localProjectTotalsObject.budgetMargin /
				localProjectTotalsObject.budgetGrandTotal) *
				100,
			2
		)
		localProjectTotalsObject.actualMarginPercentage = round(
			(localProjectTotalsObject.actualMargin /
				localProjectTotalsObject.budgetGrandTotal) *
				100,
			2
		)
		localProjectTotalsObject.provisionalMarginPercentage = round(
			(localProjectTotalsObject.provisionalMargin /
				localProjectTotalsObject.budgetGrandTotal) *
				100,
			2
		)
	}
	return localProjectTotalsObject
}

function calculateProjectDiscount(discount, totals) {
	const localDiscount = { ...discount }

	// budget discount values

	localDiscount.budgetTotalDiscountOnMargin = round(
		totals.budgetMargin - localDiscount.totalDiscount,
		2
	)
	localDiscount.budgetTotalDiscountOnMarginPercentage = round(
		(localDiscount.budgetTotalDiscountOnMargin /
			localDiscount.totalAfterDiscount) *
			100,
		2
	)
	localDiscount.budgetTotalDiscountPercentage = round(
		totals.budgetMarginPercentage -
			localDiscount.budgetTotalDiscountOnMarginPercentage,
		2
	)

	// provisonal discount values

	localDiscount.provisionalTotalDiscountOnMargin = round(
		totals.provisionalMargin - localDiscount.totalDiscount,
		2
	)
	localDiscount.provisionalTotalDiscountOnMarginPercentage = round(
		(localDiscount.provisionalTotalDiscountOnMargin /
			localDiscount.totalAfterDiscount) *
			100,
		2
	)

	localDiscount.provisionalTotalDiscountPercentage = round(
		totals.provisionalMarginPercentage -
			localDiscount.provisionalTotalDiscountOnMarginPercentage,
		2
	)

	// actual discount values

	localDiscount.actualTotalDiscountOnMargin = round(
		totals.actualMargin - localDiscount.totalDiscount,
		2
	)
	localDiscount.actualTotalDiscountOnMarginPercentage = round(
		(localDiscount.actualTotalDiscountOnMargin /
			localDiscount.totalAfterDiscount) *
			100,
		2
	)
	localDiscount.actualTotalDiscountPercentage = round(
		totals.actualMarginPercentage -
			localDiscount.actualTotalDiscountOnMarginPercentage,
		2
	)

	return localDiscount
}

function updateProjectTotalsObjectFromQuoteTotalsObject(
	projectTotalsObject,
	quoteTotalsObject
) {
	const localProjectTotalsObject = { ...projectTotalsObject }
	for (const key in quoteTotalsObject) {
		if (localProjectTotalsObject.hasOwnProperty(key)) {
			localProjectTotalsObject[key] += quoteTotalsObject[key]
			localProjectTotalsObject[key] = round(
				localProjectTotalsObject[key],
				2
			)
		}
	}
	return localProjectTotalsObject
}

function updateProjectCategoryTotalsArrayFromQuoteTotalsArray({
	projectCategoryTotalsArray,
	quoteCategoryTotalsArray,
	quoteType,
}) {
	const localProjectCategoryTotalsArray = [...projectCategoryTotalsArray]
	for (const categoryTotalObject of quoteCategoryTotalsArray) {
		const localProjectCategoryTotalsArrayIndex =
			localProjectCategoryTotalsArray.findIndex(
				(row) => row.category === categoryTotalObject.category
			)
		if (localProjectCategoryTotalsArrayIndex >= 0) {
			localProjectCategoryTotalsArray[
				localProjectCategoryTotalsArrayIndex
			].budgetTotal += categoryTotalObject.budgetTotal
			localProjectCategoryTotalsArray[
				localProjectCategoryTotalsArrayIndex
			].provisionalTotal += categoryTotalObject.provisionalTotal
			localProjectCategoryTotalsArray[
				localProjectCategoryTotalsArrayIndex
			].actualTotal += categoryTotalObject.actualTotal
		}
	}
	return localProjectCategoryTotalsArray
}

function updateProjectDiscountObjectFromQuoteDiscountObject(
	projectDiscountObject,
	quoteDiscountObject
) {
	const localProjectDiscountObject = { ...projectDiscountObject }
	localProjectDiscountObject.totalAfterDiscount = round(
		localProjectDiscountObject.totalAfterDiscount +
			quoteDiscountObject.totalAfterDiscount,
		2
	)
	localProjectDiscountObject.totalDiscount = round(
		localProjectDiscountObject.totalDiscount +
			quoteDiscountObject.totalDiscount,
		2
	)

	return localProjectDiscountObject
}

function getDiscountTemplateObject() {
	return {
		totalAfterDiscount: 0,
		totalDiscount: 0,
		budgetTotalDiscountOnMargin: 0,
		budgetTotalDiscountOnMarginPercentage: 0,
		budgetTotalDiscountPercentage: 0,
		provisionalTotalDiscountOnMargin: 0,
		provisionalTotalDiscountOnMarginPercentage: 0,
		provisionalTotalDiscountPercentage: 0,
		actualTotalDiscountOnMargin: 0,
		actualTotalDiscountOnMarginPercentage: 0,
		actualTotalDiscountPercentage: 0,
	}
}

function getTotalTemplateObject() {
	return {
		budgetSubTotal: 0,
		budgetGrandTotal: 0,
		budgetMargin: 0,
		budgetMarginPercentage: 0,
		provisionalSubTotal: 0,
		provisionalGrandTotal: 0,
		provisionalMargin: 0,
		provisionalMarginPercentage: 0,
		actualSubTotal: 0,
		actualGrandTotal: 0,
		actualMargin: 0,
		actualMarginPercentage: 0,
	}
}

function getCategoryTotalsTemplateArray({
	projectId,
	quoteType,
	franceOrInternational,
}) {
	let categories = getProjectQuoteCategories({ projectId, quoteType })

	if (quoteType === 'frenchApfp' || quoteType === 'frenchPostProd') {
		if (franceOrInternational) {
			if (franceOrInternational === 'i') {
				// get only the international categories when explicit asked for international categories
				categories = categories.filter((c) => c.isInternational)
			} else {
				// keep only the france categories as they represent all the unique categories
				categories = categories.filter((c) => !c.isInternational)
			}
		} else {
			// get only the france categories as they represent all the unique categories
			categories = categories.filter((c) => !c.isInternational)
		}
		// the name is normalized by replacing ' - EXE' with '' in the map below to make sure all the categories have the same name
		return categories.map((c) => {
			return {
				isInternational: franceOrInternational
					? c.isInternational
					: null,
				categoryPrefix: c.categoryPrefix,
				categoryRoman: c.categoryPrefix.substring(
					0,
					c.categoryPrefix.length - 1
				),
				categoryId: c.categoryId,
				category: c.category.replace(' - EXE', ''),
				category_fr: c.category_fr.replace(' - EXE', ''),
				budgetTotal: 0,
				provisionalTotal: 0,
				actualTotal: 0,
			}
		})
	}
	if (quoteType === 'photoGrid') {
		return categories.map((c) => {
			return {
				isInternational: null,
				categoryPrefix: c.subCategoryPrefix,
				categoryRoman: c.subCategoryPrefix.substring(
					0,
					c.subCategoryPrefix.length - 1
				),
				categoryId: c.subCategoryId,
				category: c.subCategory,
				category_fr: c.subCategory_fr,
				budgetTotal: 0,
				provisionalTotal: 0,
				actualTotal: 0,
			}
		})
	}
}

export { getProjectQuoteCategories, getProjectTotals }
